// Offline sound synthesis: every instrument and effect is rendered once, at
// the first user gesture, into sample buffers (no audio files to download).
// Pure functions over Float32Array so they can be unit-tested for length,
// level and silence without a browser.

export type Mono = Float32Array;

/** Deterministic noise so every visit sounds the same. */
function noiseGen(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return (((t ^ (t >>> 14)) >>> 0) / 4294967296) * 2 - 1;
  };
}

const TAU = Math.PI * 2;

/** One-pole low-pass state. */
function lowpass(cutoff: number, sr: number): (x: number) => number {
  const k = 1 - Math.exp((-TAU * cutoff) / sr);
  let y = 0;
  return (x) => (y += k * (x - y));
}

/** Resonant band-pass (state-variable filter); frequency may change per sample. */
function bandpass(sr: number, q: number): (x: number, f: number) => number {
  let low = 0;
  let band = 0;
  const damp = 1 / q;
  return (x, f) => {
    const g = 2 * Math.sin((Math.PI * Math.min(f, sr * 0.45)) / sr);
    low += g * band;
    const high = x - low - damp * band;
    band += g * high;
    return band;
  };
}

function make(sr: number, seconds: number): Mono {
  return new Float32Array(Math.ceil(sr * seconds));
}

/** Scale to a peak level (keeps headroom consistent across sounds). */
function normalize(buf: Mono, peak = 0.9): Mono {
  let max = 0;
  for (const v of buf) max = Math.max(max, Math.abs(v));
  if (max > 0) for (let i = 0; i < buf.length; i++) buf[i] = (buf[i]! / max) * peak;
  return buf;
}

/** Soft saturation for grit on impacts and the kiai. */
const drive = (x: number, k: number): number => Math.tanh(x * k) / Math.tanh(k);

// ---- Drums ----

/**
 * Odaiko: a big membrane whose pitch drops as it rings, plus the stick. Kept
 * deep: the user preferred it to a brighter drum a phone plays better.
 */
export function taiko(sr: number, freq = 62, dur = 1.1, seed = 1): Mono {
  const out = make(sr, dur);
  const n = noiseGen(seed);
  const stick = lowpass(900, sr);
  let phase = 0;
  let phase2 = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    const f = freq * (1 + 1.4 * Math.exp(-t * 28));
    phase += (TAU * f) / sr;
    phase2 += (TAU * f * 1.58) / sr;
    const body = Math.sin(phase) * Math.exp(-t * 4.2) + 0.35 * Math.sin(phase2) * Math.exp(-t * 9);
    const hit = stick(n()) * Math.exp(-t * 60) * 2.2;
    out[i] = drive(body + hit, 1.6);
  }
  return normalize(out);
}

/** Ka: stick on the rim, dry and woody. */
export function ka(sr: number, seed = 2): Mono {
  const out = make(sr, 0.08);
  const n = noiseGen(seed);
  const bp = bandpass(sr, 6);
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    out[i] = bp(n(), 2600) * Math.exp(-t * 70) + 0.4 * Math.sin(TAU * 1180 * t) * Math.exp(-t * 90);
  }
  return normalize(out, 0.8);
}

/** Shime-daiko: small, tightly tuned, a crisp high "tek". */
export function shime(sr: number, seed = 3): Mono {
  const out = make(sr, 0.2);
  const n = noiseGen(seed);
  const hp = lowpass(3000, sr);
  let phase = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    phase += (TAU * 330 * (1 + 0.2 * Math.exp(-t * 60))) / sr;
    const x = n();
    out[i] = Math.sin(phase) * Math.exp(-t * 26) + (x - hp(x)) * Math.exp(-t * 110) * 0.9;
  }
  return normalize(out, 0.8);
}

// ---- Melodic instruments ----

/**
 * Koto: Karplus-Strong plucked string with a bright pick and slow decay. The
 * loop is tuned to a fraction of a sample (the averaging filter's half sample
 * plus an allpass for the rest), so every note is in tune at any sample rate.
 */
export function koto(sr: number, freq: number, dur = 1.8, seed = 4): Mono {
  const out = make(sr, dur);
  const n = noiseGen(seed + Math.round(freq));
  const loop = sr / freq;
  const period = Math.max(2, Math.floor(loop - 0.6));
  // First-order Thiran allpass for the fraction left (0.1–1.1 samples)
  const frac = loop - 0.5 - period;
  const ap = (1 - frac) / (1 + frac);
  const line = new Float32Array(period);
  // Pluck: low-passed noise (a pick, not a hammer), shaped by the pick
  // position (a comb) for the koto's twang
  const pick = Math.floor(period * 0.18);
  const soft = lowpass(Math.min(freq * 7, sr * 0.4), sr);
  for (let i = 0; i < 64; i++) soft(n()); // settle the filter
  for (let i = 0; i < period; i++) line[i] = soft(n());
  for (let i = period - 1; i >= pick; i--) line[i] = line[i]! - 0.7 * line[i - pick]!;
  let idx = 0;
  let prev = 0;
  let apIn = 0;
  let apOut = 0;
  const loss = Math.pow(0.001, 1 / (freq * dur * 0.9));
  for (let i = 0; i < out.length; i++) {
    const cur = line[idx]!;
    const v = (cur + prev) * 0.5 * loss;
    prev = cur;
    apOut = ap * v + apIn - ap * apOut;
    apIn = v;
    line[idx] = apOut;
    out[i] = cur;
    idx = (idx + 1) % period;
  }
  // Soft attack click removal and a gentle fade at the end
  for (let i = 0; i < Math.min(out.length, 60); i++) out[i] = out[i]! * (i / 60);
  const fade = Math.floor(sr * 0.05);
  for (let i = 0; i < fade; i++) out[out.length - 1 - i] = out[out.length - 1 - i]! * (i / fade);
  return normalize(out, 0.75);
}

/** Shakuhachi: breathy bamboo flute, delayed vibrato, a breath burst to start. */
export function flute(sr: number, freq: number, dur = 1.6, seed = 5): Mono {
  const out = make(sr, dur);
  const n = noiseGen(seed + Math.round(freq));
  const breath = bandpass(sr, 3);
  let phase = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    const vib = t > 0.35 ? Math.sin(TAU * 5.3 * t) * 0.006 * Math.min(1, (t - 0.35) * 3) : 0;
    phase += (TAU * freq * (1 + vib)) / sr;
    const attack = Math.min(1, t / 0.09);
    const release = Math.min(1, (dur - t) / 0.25);
    const env = attack * release;
    const tone = Math.sin(phase) + 0.22 * Math.sin(phase * 2) + 0.08 * Math.sin(phase * 3);
    const air = breath(n(), freq * 2) * (0.35 + 0.9 * Math.exp(-t * 14));
    out[i] = (tone * 0.7 + air * 0.5) * env;
  }
  return normalize(out, 0.6);
}

/** Temple-bell "ting": inharmonic partials with long, staggered decays. */
export function bell(sr: number, freq = 1320, dur = 1.4): Mono {
  const out = make(sr, dur);
  const partials: [number, number, number][] = [
    [1, 1, 3.2],
    [2.76, 0.45, 5],
    [5.4, 0.25, 8],
    [8.93, 0.12, 12],
  ];
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    let v = 0;
    for (const [ratio, amp, decay] of partials)
      v += amp * Math.sin(TAU * freq * ratio * t) * Math.exp(-t * decay);
    out[i] = v * Math.min(1, t / 0.002);
  }
  return normalize(out, 0.55);
}

// ---- Karate sounds ----

/** The crack of a gi sleeve snapping out at the end of a strike. */
export function giSnap(sr: number, seed = 6): Mono {
  const out = make(sr, 0.07);
  const n = noiseGen(seed);
  const bp = bandpass(sr, 1.4);
  const lp = lowpass(700, sr);
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    const x = n();
    const crack = bp(x, 5200 - t * 38000) * Math.exp(-t * 95);
    const fwap = lp(x) * Math.exp(-t * 60) * 2.2;
    out[i] = crack + fwap;
  }
  return normalize(out, 0.85);
}

/** Air moving past: a band-passed noise sweep, loudest in the middle. */
export function whoosh(sr: number, dur: number, f0: number, f1: number, seed = 7): Mono {
  const out = make(sr, dur);
  const n = noiseGen(seed);
  const bp = bandpass(sr, 1.8);
  for (let i = 0; i < out.length; i++) {
    const k = i / out.length;
    out[i] = bp(n(), f0 + (f1 - f0) * k) * Math.sin(Math.PI * k) ** 1.5;
  }
  return normalize(out, 0.7);
}

/**
 * A strike landing: sub thump + mid punch + high transient, saturated.
 * `weight` 0..1 moves from a snappy punch to a deep, heavy kick.
 */
export function impact(sr: number, weight: number, seed = 8): Mono {
  const out = make(sr, 0.35);
  const n = noiseGen(seed);
  const mid = lowpass(1400 - weight * 600, sr);
  const hi = bandpass(sr, 2);
  let phase = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    phase += (TAU * (70 + 60 * (1 - weight)) * (1 + 1.5 * Math.exp(-t * 40))) / sr;
    const sub = Math.sin(phase) * Math.exp(-t * (18 - weight * 8)) * (0.8 + weight * 0.6);
    const x = n();
    const punch = mid(x) * Math.exp(-t * (55 - weight * 20)) * 2.4;
    const click = hi(x, 4200) * Math.exp(-t * 400) * 0.8;
    out[i] = drive(sub + punch + click, 2.2 + weight);
  }
  return normalize(out, 0.95);
}

interface Strike {
  dur: number;
  /** Sub thump: pitch (Hz), decay rate, level. */
  sub: [number, number, number];
  /** Low-passed body of the hit: cutoff (Hz), decay rate, level. */
  thud: [number, number, number];
  /** Skin/gi slap: band centre (Hz), decay rate, level. */
  slap: [number, number, number];
  /** Contact transient: band centre (Hz), decay rate, level. */
  click: [number, number, number];
  /** The opponent's forced exhale ("hff"), for body blows. */
  breath: number;
  drive: number;
}

/** What a landed strike sounds like, by where it lands and what lands it. */
export const STRIKES = {
  // Fist to the head: tight, bright crack
  headPunch: {
    dur: 0.22,
    sub: [110, 30, 0.5],
    thud: [2200, 70, 1.6],
    slap: [1900, 90, 1.1],
    click: [4800, 500, 1.2],
    breath: 0,
    drive: 2.6,
  },
  // Shin/instep to the head: a bigger, wetter slap
  headKick: {
    dur: 0.3,
    sub: [90, 22, 0.8],
    thud: [1600, 45, 2],
    slap: [1300, 50, 1.5],
    click: [3800, 350, 1],
    breath: 0,
    drive: 3,
  },
  // Fist into the chest or ribs: a short thud that knocks some air out
  bodyPunch: {
    dur: 0.3,
    sub: [72, 14, 1.1],
    thud: [800, 40, 2.2],
    slap: [1000, 70, 0.5],
    click: [2800, 350, 0.4],
    breath: 0.3,
    drive: 2.4,
  },
  // Foot into the midsection: deep, muffled thud and the wind knocked out
  bodyKick: {
    dur: 0.42,
    sub: [60, 10, 1.4],
    thud: [600, 30, 2.6],
    slap: [900, 60, 0.6],
    click: [2500, 300, 0.4],
    breath: 0.4,
    drive: 2.4,
  },
} satisfies Record<string, Strike>;

export type StrikeName = keyof typeof STRIKES;

/** How many takes of each strike are rendered, so repeated hits never sound alike. */
export const STRIKE_TAKES = 3;

/** Per take: how much faster its layers die away, and where its slap rings. */
const TAKES: [decay: number, slap: number][] = [
  [1, 1],
  [0.96, 1.08],
  [1.06, 0.95],
];

/**
 * A strike landing on an opponent: the sound depends on where it lands. Each
 * take has its own noise and a slightly different decay and slap.
 */
export function strike(sr: number, name: StrikeName, take = 0): Mono {
  const s: Strike = STRIKES[name];
  const [k, bright] = TAKES[take % TAKES.length]!;
  const out = make(sr, s.dur);
  const n = noiseGen(13 + take * 31);
  const thud = lowpass(s.thud[0], sr);
  const slap = bandpass(sr, 1.6);
  const click = bandpass(sr, 2);
  const breath = bandpass(sr, 1);
  let phase = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    phase += (TAU * s.sub[0] * (1 + 1.5 * Math.exp(-t * 40))) / sr;
    const x = n();
    const hit =
      Math.sin(phase) * Math.exp(-t * s.sub[1] * k) * s.sub[2] +
      thud(x) * Math.exp(-t * s.thud[1] * k) * s.thud[2] +
      slap(x, s.slap[0] * bright) * Math.exp(-t * s.slap[1] * k) * s.slap[2] +
      click(x, s.click[0] * bright) * Math.exp(-t * s.click[1]) * s.click[2];
    // Exhale: swells in after the contact, then trails off
    const bt = t - 0.025;
    const hff =
      bt > 0 ? breath(n(), 1100) * Math.min(1, bt / 0.02) * Math.exp(-bt * 14) * s.breath : 0;
    // Fade the last 10 ms so the buffer ends in silence
    const end = Math.min(1, (s.dur - t) / 0.01);
    out[i] = (drive(hit, s.drive) + hff) * end;
  }
  return normalize(out, 0.95);
}

/** A strike absorbed by a glove or a forearm: dull and short. */
export function glove(sr: number, seed = 9): Mono {
  const out = make(sr, 0.14);
  const n = noiseGen(seed);
  const lp = lowpass(420, sr);
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    out[i] = lp(n()) * Math.exp(-t * 38) * 3 + 0.5 * Math.sin(TAU * 155 * t) * Math.exp(-t * 30);
  }
  return normalize(out, 0.8);
}

/** A body blow taken: low, heavy, with a crunch. */
export function bodyBlow(sr: number, seed = 10): Mono {
  const out = make(sr, 0.5);
  const n = noiseGen(seed);
  const lp = lowpass(260, sr);
  const bp = bandpass(sr, 1.2);
  let phase = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    phase += (TAU * 55 * (1 + Math.exp(-t * 30))) / sr;
    const x = n();
    out[i] = drive(
      Math.sin(phase) * Math.exp(-t * 7) +
        lp(x) * Math.exp(-t * 14) * 2 +
        bp(x, 1600) * Math.exp(-t * 45) * 0.6,
      2,
    );
  }
  return normalize(out, 0.95);
}

// ---- Voice ----

type Formants = [number, number, number, number, number];

/** How a voice's folds move: see `Glottis`. */
interface Folds {
  /** Share of each cycle the folds are open (short = pressed and bright). */
  open: number;
  /** Share of the open phase spent snapping shut (short = sharper, brighter). */
  close: number;
  /** Jitter (±2 % of a cycle's length) and shimmer (±12 % of its strength), 0–1. */
  rough: number;
  /** How much weaker every other pulse is (vocal fry), 0–1. */
  fry: number;
}

/**
 * The voice's source: glottal pulses (Rosenberg's: the folds open smoothly
 * and snap shut), given as the airflow's slope, which is what the lips
 * radiate. Each cycle gets its own length and strength (jitter, shimmer), and
 * a strained voice alternates strong and weak pulses (vocal fry). `flow` is
 * the airflow itself (0 closed – 1 open), which the breath noise follows.
 */
class Glottis {
  flow = 0;
  private phase = 0;
  private cycle = 0;
  private period = 1;
  private amp = 1;

  constructor(
    private readonly sr: number,
    private readonly n: () => number,
    private readonly folds: Folds,
  ) {}

  /** The next sample at pitch `f0`. */
  next(f0: number): number {
    const { open, close, rough, fry } = this.folds;
    this.phase += f0 / (this.sr * this.period);
    if (this.phase >= 1) {
      this.phase -= 1;
      this.cycle++;
      this.period = 1 + this.n() * rough * 0.02;
      this.amp = (1 + this.n() * rough * 0.12) * (this.cycle % 2 ? 1 - fry : 1);
    }
    const p = this.phase;
    const opening = open * (1 - close);
    const flow =
      this.amp *
      (p < opening
        ? 0.5 * (1 - Math.cos((Math.PI * p) / opening))
        : p < open
          ? Math.cos((Math.PI * (p - opening)) / (2 * open * close))
          : 0);
    // The slope over one sample: the pulse box-filtered, so the closing snap doesn't alias
    const slope = ((flow - this.flow) * this.sr) / (f0 * 6);
    this.flow = flow;
    return slope;
  }
}

/** Formant bandwidths (Hz), F1–F5. */
const BANDWIDTHS: Formants = [110, 130, 200, 300, 400];

/**
 * The vocal tract: five formant resonators in a row (Klatt's cascade). In a
 * row they set each formant's level the way a throat does, with no hollow
 * gaps between them; each is retuned per sample as the vowel glides.
 */
function tract(sr: number): (x: number, f: Formants) => number {
  const r = BANDWIDTHS.map((bw) => Math.exp((-Math.PI * bw) / sr));
  const y1 = [0, 0, 0, 0, 0];
  const y2 = [0, 0, 0, 0, 0];
  return (x, f) => {
    let v = x;
    for (let j = 0; j < 5; j++) {
      const c = -r[j]! * r[j]!;
      const b = 2 * r[j]! * Math.cos((TAU * Math.min(f[j]!, sr * 0.45)) / sr);
      const y = (1 - b - c) * v + b * y1[j]! + c * y2[j]!;
      y2[j] = y1[j]!;
      y1[j] = y;
      v = y;
    }
    return v;
  };
}

/** Voice levels: the throat's output where the drive after it expects it. */
const VOICE = 0.45;
const BREATH = 0.075;
const GRUNT = 0.85;

type Vowel = 'a' | 'e' | 'i';

/**
 * Formants F1–F5 (Hz) of the shout: F1–F3 set between adult male and female
 * averages, F3–F5 drawn together as a shouting voice does (the ring that carries).
 */
const VOWELS: Record<Vowel, Formants> = {
  a: [850, 1400, 2600, 3100, 3700],
  e: [560, 2000, 2650, 3150, 3750],
  i: [340, 2600, 3100, 3500, 4000],
};

interface Shout {
  /** How the shout starts: a glottal click, a breathy "h" or a hissed "s". */
  onset: 'glottal' | 'h' | 's';
  /** Vowel keyframes: seconds after the voice starts, glided in between. */
  vowels: [number, Vowel][];
  /** When the voice ends (s). */
  end: number;
  /** Fade-out length; 0 cuts the voice off with a glottal stop. */
  fade: number;
  /** Pitch change across the shout (0.1 = climbs 10%). */
  rise: number;
}

/** The kiai shouts a fighter picks from. */
export const KIAIS = {
  AI: {
    onset: 'glottal',
    vowels: [
      [0, 'a'],
      [0.1, 'a'],
      [0.18, 'i'],
    ],
    end: 0.38,
    fade: 0.07,
    rise: 0.1,
  },
  EI: {
    onset: 'glottal',
    vowels: [
      [0, 'e'],
      [0.07, 'e'],
      [0.18, 'i'],
    ],
    end: 0.26,
    fade: 0.03,
    rise: 0.06,
  },
  HA: { onset: 'h', vowels: [[0, 'a']], end: 0.2, fade: 0, rise: -0.1 },
  SA: { onset: 's', vowels: [[0, 'a']], end: 0.3, fade: 0.06, rise: -0.05 },
  YA: {
    onset: 'glottal',
    vowels: [
      [0, 'i'],
      [0.04, 'a'],
    ],
    end: 0.3,
    fade: 0.06,
    rise: 0.04,
  },
  HAI: {
    onset: 'h',
    vowels: [
      [0, 'a'],
      [0.08, 'a'],
      [0.16, 'i'],
    ],
    end: 0.34,
    fade: 0.06,
    rise: 0.1,
  },
} satisfies Record<string, Shout>;

export type KiaiName = keyof typeof KIAIS;

/** Formants at a time along the vowel keyframes. */
function formantsAt(vowels: [number, Vowel][], t: number): Formants {
  let [t0, v0] = vowels[0]!;
  for (const [t1, v1] of vowels) {
    if (t < t1) {
      const k = t1 > t0 ? (t - t0) / (t1 - t0) : 1;
      const a = VOWELS[v0];
      const b = VOWELS[v1];
      return a.map((f, j) => f + (b[j]! - f) * k) as Formants;
    }
    [t0, v0] = [t1, v1];
  }
  return VOWELS[v0];
}

/**
 * Kiai: a short, sharp shout that reads as neither a man's nor a woman's.
 * An onset (glottal attack, "h" or "s"), gliding vowels, then a fade or a hard
 * glottal stop. The pitch (~300 Hz) and formants sit where shouted adult male
 * and female voices overlap; pressed, jittery pulses and breath hide the rest.
 */
export function kiai(sr: number, name: KiaiName = 'AI', pitch = 300, seed = 11): Mono {
  const sh: Shout = KIAIS[name];
  const out = make(sr, sh.end + 0.02);
  const n = noiseGen(seed);
  const glottis = new Glottis(sr, n, { open: 0.45, close: 0.12, rough: 0.5, fry: 0 });
  const throat = tract(sr);
  // An "h" is breath through the same throat: it takes on the vowel's colour
  const breath = tract(sr);
  const click = bandpass(sr, 2);
  const hiss = bandpass(sr, 1.5);
  const start = sh.onset === 's' ? 0.06 : sh.onset === 'h' ? 0.013 : 0;
  const len = sh.end - start;
  let jitter = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    const vt = t - start;
    // Pitch: snaps up on the attack, then climbs or drops, with a strained wobble
    jitter += (n() - jitter) * 0.002;
    const wobble = Math.sin(TAU * 5.5 * t) * 0.012 * Math.min(1, t / 0.2);
    const k = Math.min(1, Math.max(0, vt / len));
    const p =
      pitch *
      (1 + 0.12 * Math.min(1, Math.max(0, vt) / 0.02) + sh.rise * k + wobble + jitter * 0.06);
    // Forced voice: pressed pulses (open a short while: bright), the first one
    // hard on a glottal attack, with breath that puffs out as the folds open
    const pulse = glottis.next(p) * (sh.onset === 'glottal' && vt < 1 / p ? 1.5 : 1);
    const source = pulse + n() * (0.12 + 0.9 * glottis.flow);
    const formants = formantsAt(sh.vowels, Math.max(0, vt));
    const voiced = throat(source, formants) * VOICE;
    // Voice at full force within 4 ms; fades, or stops dead at the glottis
    const left = sh.end - t;
    const release = sh.fade > 0 ? left / (sh.fade * 1.4) : left / 0.012;
    const env = vt <= 0 ? 0 : Math.min(1, vt / 0.004) * Math.min(1, Math.max(0, release));
    // After the drive: a slight sag and a clean fade (saturation hides the pre-drive fade)
    const cut = sh.fade > 0 ? Math.max(0, left / sh.fade) : Math.max(0, left / 0.008);
    const tail = (1 - 0.2 * k) * Math.min(1, cut) ** 1.5;
    // Onsets
    let on = 0;
    if (sh.onset === 'glottal' && t < 0.01) on = click(n(), 1800) * Math.exp(-t * 400) * 0.3;
    else if (sh.onset === 'h' && t < 0.025)
      on = breath(n(), formantsAt(sh.vowels, 0)) * (t / 0.025) * BREATH;
    else if (sh.onset === 's' && t < 0.075)
      on = hiss(n(), 5500) * Math.min(1, t / 0.02) * Math.min(1, (0.075 - t) / 0.02) * 0.45;
    out[i] = drive(voiced * env * 1.4, 3.2) * tail + on;
  }
  return normalize(out, 0.88);
}

interface Grunt {
  /** Formants (Hz) of the sound pushed out: open "uh", nasal "hng", breathy "huf". */
  formants: [number, number, number];
  /** Share of breath noise in the voice (0 = clean, 1 = mostly air). */
  air: number;
  /** Low-pass on the voice (Hz): a closed mouth muffles it. */
  muffle: number;
  /** Trailing "f" as the lips close on the last air. */
  ff: number;
  dur: number;
}

/** What an opponent lets out when a hard strike lands (never an "ouch"). */
export const GRUNTS = {
  UH: { formants: [560, 1450, 2500], air: 0.45, muffle: 3000, ff: 0, dur: 0.2 },
  HNG: { formants: [280, 1900, 2600], air: 0.3, muffle: 1300, ff: 0, dur: 0.18 },
  HUF: { formants: [620, 1200, 2400], air: 0.8, muffle: 2400, ff: 0.5, dur: 0.24 },
  UGH: { formants: [520, 1150, 2400], air: 0.5, muffle: 2200, ff: 0, dur: 0.22 },
  MPF: { formants: [260, 1000, 2400], air: 0.4, muffle: 900, ff: 0.3, dur: 0.2 },
  OOF: { formants: [450, 900, 2300], air: 0.6, muffle: 2000, ff: 0.4, dur: 0.23 },
} satisfies Record<string, Grunt>;

/** How many harshness steps each grunt is rendered at (a longer combo sounds harsher). */
export const GRUNT_HARSHNESS = 3;

export type GruntName = keyof typeof GRUNTS;

/**
 * A grunt: air knocked out of an opponent by a hard strike. A short rush of
 * breath, then a strained, rough voice that falls in pitch and dies quickly.
 * `harsh` (0–1) strains it further: more drive, air and vocal fry, longer.
 */
export function grunt(sr: number, name: GruntName, harsh = 0, pitch = 190, seed = 14): Mono {
  const g: Grunt = GRUNTS[name];
  const dur = g.dur + 0.05 * harsh;
  const air = Math.min(1, g.air + 0.2 * harsh);
  const out = make(sr, dur);
  const n = noiseGen(seed);
  const glottis = new Glottis(sr, n, {
    open: 0.6 - 0.15 * harsh,
    close: 0.2,
    rough: 0.6 + 0.4 * harsh,
    fry: 0.55 * harsh,
  });
  const throat = tract(sr);
  const formants: Formants = [...g.formants, 3500, 4400];
  const lp = lowpass(g.muffle * (1 + 0.4 * harsh), sr);
  const rush = bandpass(sr, 1.2);
  const fric = bandpass(sr, 1.5);
  const onset = 0.012;
  const len = dur - onset;
  let jitter = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    const vt = t - onset;
    const k = Math.min(1, Math.max(0, vt / len));
    // Strained and rough: pitch falls, with heavy jitter (a hint of vocal fry)
    jitter += (n() - jitter) * (0.004 + 0.006 * harsh);
    const p =
      pitch * (1 + (0.08 + 0.1 * harsh) * (1 - k) - 0.25 * k + jitter * (0.15 + 0.3 * harsh));
    // Lax pulses, pressed harder and rougher when strained, every other one
    // weaker (vocal fry); the air rushing through puffs with each opening
    const pulse = glottis.next(p);
    const source = pulse * (1 - air * 0.6) + n() * air * (0.5 + 0.8 * glottis.flow);
    const voiced = lp(throat(source, formants)) * GRUNT;
    // Snaps on, then dies away fast
    const env =
      vt <= 0
        ? 0
        : Math.min(1, vt / 0.004) * Math.exp(-vt * (9 - 3 * harsh)) * Math.min(1, (dur - t) / 0.03);
    // The air forced out just before the voice catches
    const rushed = t < onset + 0.02 ? rush(n(), 1400) * Math.exp(-t * 60) * 0.5 : 0;
    const ff = g.ff > 0 ? fric(n(), 4200) * g.ff * k * env : 0;
    // Fade after the drive: saturation would flatten a pre-drive decay
    out[i] = drive(voiced * (1.6 + harsh), 2.2 + 2.3 * harsh) * env + rushed + ff;
  }
  return normalize(out, 0.85);
}

/** Wood-block click for menu buttons. */
export function woodClick(sr: number): Mono {
  const out = make(sr, 0.07);
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    out[i] = (Math.sin(TAU * 920 * t) + 0.6 * Math.sin(TAU * 1480 * t)) * Math.exp(-t * 80);
  }
  return normalize(out, 0.6);
}

/** Rising wind-up whoosh: the audio tell before an opponent attacks. */
export function windup(sr: number, seed = 12): Mono {
  const out = make(sr, 0.38);
  const n = noiseGen(seed);
  const bp = bandpass(sr, 4);
  for (let i = 0; i < out.length; i++) {
    const k = i / out.length;
    // Swells to a peak just before the strike, then drops away quickly
    const env = k < 0.8 ? (k / 0.8) ** 1.6 : (1 - k) / 0.2;
    out[i] = bp(n(), 280 + 1500 * k * k) * env;
  }
  return normalize(out, 0.6);
}

/** Short LED counter beep for the impact pad. */
export function beep(sr: number, freq: number): Mono {
  const out = make(sr, 0.045);
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    const sq = Math.sin(TAU * freq * t) + Math.sin(TAU * freq * 3 * t) / 3;
    out[i] = sq * Math.min(1, t / 0.002) * Math.min(1, (0.045 - t) / 0.01);
  }
  return normalize(out, 0.4);
}

// ---- Rooms ----

export interface Room {
  seconds: number;
  /** Decay rate (larger = shorter tail). */
  decay: number;
  /** Air absorption: low-pass cutoff of the tail, in Hz. */
  tone: number;
  /** Early reflections (delay s, gain) — the character of the space. */
  early: [number, number][];
}

export const ROOMS: Record<'dojo' | 'park' | 'rooftop', Room> = {
  // Wooden hall: warm, medium tail, close walls
  dojo: {
    seconds: 1.4,
    decay: 3.6,
    tone: 5200,
    early: [
      [0.011, 0.5],
      [0.019, 0.35],
      [0.031, 0.25],
    ],
  },
  // Open air: almost no tail, one distant reflection off the trees
  park: { seconds: 0.6, decay: 9, tone: 3500, early: [[0.07, 0.12]] },
  // City: long, dark tail with a slap echo off the buildings opposite
  rooftop: {
    seconds: 2.2,
    decay: 2.3,
    tone: 2600,
    early: [
      [0.18, 0.22],
      [0.36, 0.1],
    ],
  },
};

/** A stereo impulse response for a convolution reverb. */
export function impulse(sr: number, room: Room, seed = 13): [Mono, Mono] {
  const channels: [Mono, Mono] = [make(sr, room.seconds), make(sr, room.seconds)];
  channels.forEach((ch, c) => {
    const n = noiseGen(seed + c * 101);
    const lp = lowpass(room.tone, sr);
    for (let i = 0; i < ch.length; i++) {
      const t = i / sr;
      ch[i] = lp(n()) * Math.exp(-t * room.decay);
    }
    for (const [d, g] of room.early) {
      const at = Math.floor((d + c * 0.0017) * sr);
      if (at < ch.length) ch[at] = ch[at]! + g;
    }
    normalize(ch, 0.5);
  });
  return channels;
}

// ---- Scale ----

/** Miyako-bushi (in) scale on D: D, E♭, G, A, B♭ — semitones from the root. */
const IN_SCALE = [0, 1, 5, 7, 8];

/** Frequency of scale degree `n` (0 = D3), across octaves. */
export function scaleFreq(n: number, root = 146.83): number {
  const oct = Math.floor(n / IN_SCALE.length);
  const deg = ((n % IN_SCALE.length) + IN_SCALE.length) % IN_SCALE.length;
  return root * 2 ** (oct + IN_SCALE[deg]! / 12);
}
